import { CheckOutlined, CloseOutlined, EditOutlined, InboxOutlined } from '@ant-design/icons';
import { Button, Flex, message, Space, Tooltip } from 'antd';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

import ServiceOrderItems from '../ServiceOrderItems/ServiceOrderItems';
import ServiceOrderLoadedItems from './ServiceOrderLoadedItems';
import { hasInvalidServiceOrderItemsDistribution } from './serviceOrderShowHelpers';

// El Show puede traer tickets expandidos (objetos), pero los repositorios de items esperan ids.
const getTicketIds = (tickets: ServiceOrder['tickets']): number[] =>
  (tickets ?? []).map((ticket: any) => (typeof ticket === 'number' ? ticket : ticket.id));

interface ServiceOrderItemsSectionProps {
  serviceOrder: ServiceOrder;
  serviceOrderItems: ServiceOrderItem[];
  setServiceOrderItems: Dispatch<SetStateAction<ServiceOrderItem[]>>;
  blockEdit: boolean;
  refetch: () => Promise<any>;
}

/**
 * Edición de "Elementos utilizados" inline dentro de la misma sección del
 * Show: al presionar "Editar elementos" se reemplaza la tabla de solo
 * lectura (ServiceOrderLoadedItems) por el form de distribución de
 * cantidades (ServiceOrderItems), sin abrir un Drawer aparte. Al cancelar o
 * guardar, ServiceOrderLoadedItems vuelve a montarse y refetchea desde el
 * backend, descartando cualquier cambio no guardado.
 */
const ServiceOrderItemsSection = (props: ServiceOrderItemsSectionProps) => {
  const { serviceOrder, serviceOrderItems, setServiceOrderItems, refetch } = props;
  const { t } = useTranslation();
  const { serviceOrderRepository } = useRepositoriesContext();
  const { userWorkArea } = useUserContext();
  const canEdit = !props.blockEdit && serviceOrder.status === SERVICE_ORDER_STATUS.REALIZADA;
  const isInvalidDistribution = useMemo(
    () => hasInvalidServiceOrderItemsDistribution(serviceOrderItems),
    [serviceOrderItems],
  );
  // Servicio al cliente debe completar la distribución antes de poder avanzar de estado:
  // se fuerza la edición (sin opción de cancelar) solo si hay elementos sin distribuir.
  // Si ya está completo, se deja la vista de solo lectura normal.
  const forceEdit =
    canEdit && userWorkArea === USER_WORK_AREA.CUSTOMER_SERVICE && isInvalidDistribution;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasInvalidDistribution, setHasInvalidDistribution] = useState(false);
  const ticketIds = useMemo(() => getTicketIds(serviceOrder.tickets), [serviceOrder.tickets]);

  // serviceOrderItems se carga de forma async en la vista de lectura (ServiceOrderLoadedItems);
  // recién cuando llega se puede evaluar forceEdit y, si corresponde, pasar a edición.
  useEffect(() => {
    if (forceEdit) setEditing(true);
  }, [forceEdit]);
  const handleSave = async () => {
    setSaving(true);

    try {
      await serviceOrderRepository.saveServiceOrderItems(serviceOrderItems, serviceOrder.id);
      await refetch();
      message.success(t('Orden de servicio actualizada correctamente'));
      setEditing(false);
    } catch {
      message.error(t('Ocurrió un error al guardar los elementos utilizados'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ShowSection
      title={t('Elementos utilizados')}
      className="ServiceOrderShowItems"
      icon={<InboxOutlined />}
      headerExtra={
        !editing &&
        canEdit && (
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>
            {t('Editar elementos')}
          </Button>
        )
      }>
      {editing ? (
        <>
          <ServiceOrderItems
            tickets={ticketIds}
            serviceOrderItems={serviceOrderItems}
            setServiceOrderItems={setServiceOrderItems}
            hasExistingServiceOrder
            onInvalidDistributionChange={setHasInvalidDistribution}
          />
          <Flex justify="flex-end" style={{ marginTop: 16 }}>
            <Space>
              {!forceEdit && (
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  disabled={saving}
                  onClick={() => setEditing(false)}>
                  {t('Cancelar')}
                </Button>
              )}
              <Tooltip
                title={
                  hasInvalidDistribution
                    ? t('Complete las cantidades de los elementos enviados')
                    : ''
                }>
                <Button
                  size="small"
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={saving}
                  disabled={hasInvalidDistribution}
                  onClick={() => void handleSave()}>
                  {t(forceEdit ? 'Guardar elementos' : 'Guardar')}
                </Button>
              </Tooltip>
            </Space>
          </Flex>
        </>
      ) : (
        <ServiceOrderLoadedItems
          serviceOrderId={serviceOrder.id}
          serviceOrderItems={serviceOrderItems}
          setServiceOrderItems={setServiceOrderItems}
        />
      )}
    </ShowSection>
  );
};

export default ServiceOrderItemsSection;
